import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { VehicleDocType } from './vehicle-doc-cache';

const DB_NAME = 'fleetup-driver';
const DB_VERSION = 3;

export interface FleetupDriverDB extends DBSchema {
  auth: {
    key: 'profile';
    // `payload` (not `profile`) so the field name doesn't shadow the store name —
    // readers do `row.payload` instead of the awkward `row.profile`. Matches the
    // readCache convention.
    value: { payload: unknown; lastLoginAt: number };
  };
  readCache: {
    // PR-2 owner: must implement TTL or LRU eviction on writes — without one,
    // this store grows until the browser silently evicts under quota pressure
    // (potentially mid-trip). The by_fetchedAt index supports cursor-based sweep.
    key: string;                   // url
    value: { url: string; payload: unknown; fetchedAt: number };
    indexes: { by_fetchedAt: number };
  };
  pendingMutations: {
    key: string;                   // uuid
    value: {
      id: string;
      idempotencyKey: string;
      op: string;                   // e.g. 'submit-duty-slip'
      url: string;
      method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
      body: unknown;
      createdAt: number;
      attempts: number;
      lastAttemptAt?: number;
      lastError?: string;
    };
    indexes: { by_createdAt: number };
  };
  activeDrafts: {
    key: string;                   // bookingId
    value: {
      bookingId: string;
      kind: 'duty-slip' | 'expense-form' | 'trip-state';
      draft: unknown;
      updatedAt: number;
    };
  };
  pushNotifications: {
    key: string;                   // id
    value: {
      id: string;
      title: string;
      body: string;
      receivedAt: number;
      readAt?: number;
      link?: string;
    };
  };
  kycDocs: {
    key: 'photo' | 'dl' | 'aadhaar' | 'pan';
    value: {
      doc: 'photo' | 'dl' | 'aadhaar' | 'pan';
      mime: string;
      blob: Blob;
      cachedAt: number;
    };
  };
  vehicleComplianceDocs: {
    key: string;  // composite `${vehicleId}::${type}`
    value: {
      key: string;
      vehicleId: string;
      type: VehicleDocType;
      mime: string;
      blob: Blob;
      cachedAt: number;
    };
  };
}

let _db: IDBPDatabase<FleetupDriverDB> | null = null;

export async function db(): Promise<IDBPDatabase<FleetupDriverDB>> {
  if (_db) return _db;
  _db = await openDB<FleetupDriverDB>(DB_NAME, DB_VERSION, {
    // IDB upgrade callback fires the ENTIRE body on every version transition,
    // not just "new" stores. Each createObjectStore must be guarded by
    // `oldVersion < <version-it-was-introduced-in>` or it will throw
    // ConstraintError ("store already exists") for upgrading users.
    upgrade(d, oldVersion) {
      if (oldVersion < 1) {
        d.createObjectStore('auth');
        const rc = d.createObjectStore('readCache', { keyPath: 'url' });
        rc.createIndex('by_fetchedAt', 'fetchedAt');
        const pm = d.createObjectStore('pendingMutations', { keyPath: 'id' });
        pm.createIndex('by_createdAt', 'createdAt');
        d.createObjectStore('activeDrafts', { keyPath: 'bookingId' });
        d.createObjectStore('pushNotifications', { keyPath: 'id' });
      }
      if (oldVersion < 2) {
        d.createObjectStore('kycDocs', { keyPath: 'doc' });
      }
      if (oldVersion < 3) {
        d.createObjectStore('vehicleComplianceDocs', { keyPath: 'key' });
      }
    },
    // Drop the cached handle if another tab triggers a versionchange or the
    // browser closes the connection (mobile Safari does this on background
    // → foreground). Without this, the singleton can orphan and every
    // subsequent call fails with "The database connection is closing".
    blocking() {
      _db?.close();
      _db = null;
    },
    terminated() {
      _db = null;
    },
  });
  return _db;
}

export async function clearAllStores(): Promise<void> {
  const handle = await db();
  const stores = [
    'auth',
    'readCache',
    'pendingMutations',
    'activeDrafts',
    'pushNotifications',
    'kycDocs',
    'vehicleComplianceDocs',
  ] as const;
  const tx = handle.transaction(stores, 'readwrite');
  await Promise.all(stores.map((s) => tx.objectStore(s).clear()));
  await tx.done;
}

export async function requestPersistentStorage(): Promise<boolean> {
  if (!('storage' in navigator) || !('persist' in navigator.storage)) return false;
  try {
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}
